import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  deleteUser as firebaseDeleteUser,
  updatePassword,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, deleteDoc, writeBatch, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from './firebase';
import {
  Transaction,
  CategorizedTransaction,
  MonthSummary,
  Category,
  NeedsSubcategory,
  FeelingScore,
  SpendingProfile,
  UserProfile,
} from './types';
import { calculateMonthSummaries } from './mockData';

interface AppContextType {
  // Auth
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  sendMagicLink: (email: string) => Promise<void>;
  completeMagicLinkSignIn: (email: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (newPassword: string) => Promise<void>;
  deleteAccount: () => Promise<void>;

  // Transactions
  transactions: Transaction[];
  categorizedTransactions: CategorizedTransaction[];
  monthSummaries: MonthSummary[];
  isConnected: boolean;
  connectAccounts: () => void;
  handlePlaidSuccess: (publicToken: string) => Promise<void>;
  categorizeTransaction: (
    transactionId: string,
    category: Category,
    subcategory: NeedsSubcategory | undefined,
    feelingScore: FeelingScore
  ) => Promise<void>;
  getUncategorizedTransactions: () => Transaction[];
  getSpendingProfile: () => SpendingProfile;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Auth state
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Transaction state
  const [isConnected, setIsConnected] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [categorizedTransactions, setCategorizedTransactions] = useState<CategorizedTransaction[]>(
    []
  );
  const [monthSummaries, setMonthSummaries] = useState<MonthSummary[]>([]);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Load user profile from Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserProfile({
            uid: user.uid,
            email: user.email!,
            firstName: data.firstName,
            lastName: data.lastName,
            createdAt: data.createdAt?.toDate() || new Date(),
            hasConnectedAccounts: data.hasConnectedAccounts || false,
          });
          setIsConnected(data.hasConnectedAccounts || false);

          // Load existing transactions if user has connected accounts
          if (data.hasConnectedAccounts) {
            await loadTransactions(user.uid);
            await loadCategorizedTransactions(user.uid);
          }
        }
      } else {
        setUserProfile(null);
        setIsConnected(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Auth functions
  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Check if user profile already exists (from other app)
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        // User exists from another app - add MMT flag
        await updateDoc(userDocRef, {
          mmt_app: true,
        });
      } else {
        // Create new user profile in Firestore
        await setDoc(userDocRef, {
          email: user.email,
          firstName,
          lastName,
          createdAt: new Date(),
          hasConnectedAccounts: false,
          mmt_app: true,
        });
      }
    } catch (error: any) {
      // Handle specific Firebase auth errors
      if (error.code === 'auth/email-already-in-use') {
        throw new Error('This email already has an account. Please sign in instead.');
      }
      // Re-throw other errors
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Set MMT flag when user signs in
    const userDocRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const existingData = userDoc.data();
      // Only update if mmt_app is not already true
      if (!existingData.mmt_app) {
        await updateDoc(userDocRef, {
          mmt_app: true,
        });
      }
    }
  };

  const signOut = async () => {
    await firebaseSignOut(auth);
    // Clear local state
    setTransactions([]);
    setCategorizedTransactions([]);
    setMonthSummaries([]);
    setIsConnected(false);
  };

  const sendMagicLink = async (email: string) => {
    // Call our API endpoint which sends via Resend with MMT branding
    const response = await fetch('/api/send-magic-link', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to send magic link');
    }

    // Store email for later use
    window.localStorage.setItem('emailForSignIn', email);
  };

  const completeMagicLinkSignIn = async (email: string) => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let emailToUse = email;

      if (!emailToUse) {
        emailToUse = window.localStorage.getItem('emailForSignIn') || '';
      }

      const result = await signInWithEmailLink(auth, emailToUse, window.location.href);
      window.localStorage.removeItem('emailForSignIn');

      // Set MMT flag when user signs in via magic link
      const user = result.user;
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const existingData = userDoc.data();
        // Only update if mmt_app is not already true
        if (!existingData.mmt_app) {
          await updateDoc(userDocRef, {
            mmt_app: true,
          });
        }
      } else {
        // Create new user profile for magic link sign-in
        await setDoc(userDocRef, {
          email: user.email,
          createdAt: new Date(),
          hasConnectedAccounts: false,
          mmt_app: true,
        });
      }
    }
  };

  const resetPassword = async (email: string) => {
    await sendPasswordResetEmail(auth, email);
  };

  const changePassword = async (newPassword: string) => {
    if (currentUser) {
      await updatePassword(currentUser, newPassword);
    }
  };

  const deleteAccount = async () => {
    if (currentUser) {
      // Delete user data from Firestore
      await deleteDoc(doc(db, 'users', currentUser.uid));
      // Delete user auth account
      await firebaseDeleteUser(currentUser);
    }
  };

  // Transaction functions
  const connectAccounts = async () => {
    // This will be called by the Plaid Link component
    // The actual Plaid integration happens in Landing.tsx
    console.log('connectAccounts called - Plaid Link will handle the flow');
  };

  const handlePlaidSuccess = async (publicToken: string) => {
    try {
      console.log('🔄 Exchanging public token...');

      // Call our Plaid API to exchange the public token
      const response = await fetch('https://api.neurofininc.com/api/exchange_public_token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ public_token: publicToken }),
      });

      const { access_token, item_id } = await response.json();

      if (!currentUser) throw new Error('No user logged in');

      // Save connection to Firebase
      await setDoc(doc(db, 'connections', item_id), {
        uid: currentUser.uid,
        access_token,
        item_id,
        created_at: new Date(),
        cursor: null, // Will be updated after first sync
      });

      console.log('✅ Connection saved to Firebase');

      // Sync transactions
      await syncTransactions(item_id, access_token);

      // Update user profile
      await updateDoc(doc(db, 'users', currentUser.uid), {
        hasConnectedAccounts: true,
      });

      setIsConnected(true);

    } catch (error) {
      console.error('❌ Error in Plaid success handler:', error);
      throw error;
    }
  };

  const syncTransactions = async (itemId: string, accessToken: string, cursor?: string) => {
    try {
      console.log('🔄 Syncing transactions...');

      const response = await fetch('https://api.neurofininc.com/api/transactions/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_token: accessToken,
          cursor: cursor || null,
        }),
      });

      const { added, cursor: newCursor } = await response.json();
      // TODO: Handle modified and removed transactions in future updates

      if (!currentUser) throw new Error('No user logged in');

      // Save transactions to Firebase
      const batch = writeBatch(db);

      added.forEach((txn: any) => {
        const txnRef = doc(collection(db, 'plaid_transactions'));
        batch.set(txnRef, {
          ...txn,
          uid: currentUser.uid,
          item_id: itemId,
          timestamp: new Date(txn.date),
        });
      });

      await batch.commit();

      console.log(`✅ Synced ${added.length} transactions`);

      // Update cursor in connection
      await updateDoc(doc(db, 'connections', itemId), {
        cursor: newCursor,
        last_sync: new Date(),
      });

      // Load transactions into state
      await loadTransactions();

    } catch (error) {
      console.error('❌ Error syncing transactions:', error);
      throw error;
    }
  };

  const loadTransactions = async (userId?: string) => {
    const uid = userId || currentUser?.uid;
    if (!uid) return;

    try {
      const q = query(
        collection(db, 'plaid_transactions'),
        where('uid', '==', uid)
      );

      const snapshot = await getDocs(q);
      const txns: Transaction[] = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().timestamp?.toDate() || new Date(),
      } as Transaction));

      setTransactions(txns);
      setMonthSummaries(calculateMonthSummaries(txns));

      console.log(`✅ Loaded ${txns.length} transactions from Firebase`);

    } catch (error) {
      console.error('❌ Error loading transactions:', error);
    }
  };

  const loadCategorizedTransactions = async (userId?: string) => {
    const uid = userId || currentUser?.uid;
    if (!uid) return;

    try {
      const q = query(
        collection(db, 'categorized_transactions'),
        where('uid', '==', uid)
      );

      const snapshot = await getDocs(q);
      const categorized: CategorizedTransaction[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          transactionId: data.transactionId,
          category: data.category,
          subcategory: data.subcategory,
          feelingScore: data.feelingScore,
          amount: data.amount,
          date: data.date,
          name: data.name,
        } as CategorizedTransaction;
      });

      setCategorizedTransactions(categorized);
      console.log(`✅ Loaded ${categorized.length} categorized transactions from Firebase`);

    } catch (error) {
      console.error('❌ Error loading categorized transactions:', error);
    }
  };

  const categorizeTransaction = async (
    transactionId: string,
    category: Category,
    subcategory: NeedsSubcategory | undefined,
    feelingScore: FeelingScore
  ) => {
    const transaction = transactions.find((t) => t.id === transactionId);
    if (!transaction || !currentUser) return;

    const newCategorization: CategorizedTransaction = {
      transactionId,
      category,
      subcategory,
      feelingScore,
      amount: transaction.amount,
      date: transaction.date,
      name: transaction.name,
    };

    // Update local state
    setCategorizedTransactions((prev) => {
      const existing = prev.findIndex((ct) => ct.transactionId === transactionId);
      if (existing !== -1) {
        const updated = [...prev];
        updated[existing] = newCategorization;
        return updated;
      }
      return [...prev, newCategorization];
    });

    // Save to Firebase (exclude undefined subcategory for Firestore compatibility)
    try {
      const firestoreData: any = {
        transactionId,
        category,
        feelingScore,
        amount: transaction.amount,
        date: transaction.date,
        name: transaction.name,
        uid: currentUser.uid,
        categorizedAt: new Date(),
      };

      // Only include subcategory if it exists (Firestore doesn't allow undefined)
      if (subcategory) {
        firestoreData.subcategory = subcategory;
      }

      await setDoc(doc(db, 'categorized_transactions', transactionId), firestoreData);
      console.log(`✅ Saved categorization for transaction ${transactionId}`);
    } catch (error) {
      console.error('❌ Error saving categorization:', error);
    }
  };

  const getUncategorizedTransactions = (): Transaction[] => {
    const categorizedIds = new Set(categorizedTransactions.map((ct) => ct.transactionId));

    // Filter to last 3 calendar months only
    const now = new Date();
    const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);

    return transactions.filter((t) => {
      const txDate = new Date(t.date);
      return (
        t.type !== 'income' &&
        !categorizedIds.has(t.id) &&
        txDate >= threeMonthsAgo
      );
    });
  };

  const getSpendingProfile = (): SpendingProfile => {
    if (categorizedTransactions.length === 0) {
      return {
        totalSpending: 0,
        overallScore: 3,
        categoryBreakdowns: [],
        needsBreakdown: [],
      };
    }

    // Calculate total spending
    const totalSpending = categorizedTransactions.reduce((sum, ct) => sum + ct.amount, 0);

    // Calculate overall average feeling score
    const overallScore =
      categorizedTransactions.reduce((sum, ct) => sum + ct.feelingScore, 0) /
      categorizedTransactions.length;

    // Calculate category breakdowns
    const categoryMap: Record<
      string,
      { amount: number; scores: number[] }
    > = {};

    categorizedTransactions.forEach((ct) => {
      if (!categoryMap[ct.category]) {
        categoryMap[ct.category] = { amount: 0, scores: [] };
      }
      categoryMap[ct.category].amount += ct.amount;
      categoryMap[ct.category].scores.push(ct.feelingScore);
    });

    const categoryBreakdowns = Object.entries(categoryMap).map(([category, data]) => ({
      category,
      amount: data.amount,
      averageFeeling: data.scores.reduce((a, b) => a + b, 0) / data.scores.length,
    }));

    // Calculate needs subcategory breakdowns
    const needsMap: Record<string, { amount: number; scores: number[] }> = {};

    categorizedTransactions
      .filter((ct) => ct.category === 'Needs' && ct.subcategory)
      .forEach((ct) => {
        const subcategory = ct.subcategory!;
        if (!needsMap[subcategory]) {
          needsMap[subcategory] = { amount: 0, scores: [] };
        }
        needsMap[subcategory].amount += ct.amount;
        needsMap[subcategory].scores.push(ct.feelingScore);
      });

    const needsBreakdown = Object.entries(needsMap).map(([category, data]) => ({
      category,
      amount: data.amount,
      averageFeeling: data.scores.reduce((a, b) => a + b, 0) / data.scores.length,
    }));

    return {
      totalSpending,
      overallScore,
      categoryBreakdowns,
      needsBreakdown,
    };
  };

  return (
    <AppContext.Provider
      value={{
        currentUser,
        userProfile,
        loading,
        signUp,
        signIn,
        signOut,
        sendMagicLink,
        completeMagicLinkSignIn,
        resetPassword,
        changePassword,
        deleteAccount,
        transactions,
        categorizedTransactions,
        monthSummaries,
        isConnected,
        connectAccounts,
        handlePlaidSuccess,
        categorizeTransaction,
        getUncategorizedTransactions,
        getSpendingProfile,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
